import { useState } from "react";
import { Button, Dialog, DialogContent, Box, Avatar, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { PiArrowsClockwise, PiCheckCircleFill } from "react-icons/pi";

interface CardFormData {
    nameOnCard: string;
    cardNumber: string;
    expiryDate: string;
    cvv: string;
}

interface ConfirmOrderChangeDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    loading: boolean;
    onConfirm: () => void;
    differenceType: 'collect' | 'refund' | 'none';
    differenceAmount: number;
    customerName: string;
    cardLogoUrl?: string;
    currency?: string;
    chargeFailed?: boolean;
    onEditPaymentInfo?: (cardData: CardFormData) => void;
    onSendFailedNotification?: () => void;
    blocked?: boolean;
}

export default function ConfirmOrderChangeDialog(props: ConfirmOrderChangeDialogProps) {
    const { 
        open, 
        setOpen, 
        loading, 
        onConfirm,
        differenceType,
        differenceAmount,
        customerName,
        cardLogoUrl,
        currency = 'CAD',
        chargeFailed = false,
        onEditPaymentInfo,
        onSendFailedNotification,
        blocked = false
    } = props;

    const [selectedFailedOption, setSelectedFailedOption] = useState<'retry' | 'edit' | 'notify' | null>('retry');
    const [showCardForm, setShowCardForm] = useState(false);
    const [cardFormData, setCardFormData] = useState<CardFormData>({
        nameOnCard: '',
        cardNumber: '',
        expiryDate: '',
        cvv: ''
    });

    const formatPrice = (price: number | string, curr: string = 'CAD') => {
        const formattedNumber = Number(price).toLocaleString('en-CA', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        });
        return `$${formattedNumber}`;
    };

    const getOptionText = () => {
        if (differenceType === 'collect') {
            return {
                title: `Charge ${formatPrice(differenceAmount)} To ${customerName}`,
                subtitle: `Amount To Charge: ${formatPrice(differenceAmount)}`
            };
        } else if (differenceType === 'refund') {
            return {
                title: `Refund ${formatPrice(differenceAmount)} To ${customerName}`,
                subtitle: `Amount To Refund: ${formatPrice(differenceAmount)}`
            };
        }
        return {
            title: 'No payment changes required',
            subtitle: ''
        };
    };

    const optionText = getOptionText();

    return (
        <Dialog
            open={open}
            onClose={() => !loading && setOpen(false)}
        >
            <DialogContent style={{ padding: '0', maxWidth: '530px', minWidth: '500px' }}>
                <div className="confirm_change_dialog_content" style={{ padding: '27px', textAlign: 'center' }}>
                    
                    {/* Icon */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                        <Avatar sx={{ bgcolor: '#FFF8E6', width: 56, height: 56 }}>
                            <PiArrowsClockwise color="#E5A800" size={28} />
                        </Avatar>
                    </div>

                    {/* Title */}
                    <h3 style={{ 
                        fontSize: '20px', 
                        fontWeight: '600', 
                        color: '#1A1A1A', 
                        marginBottom: '12px',
                        marginTop: '0'
                    }}>
                        Confirm Changes
                    </h3>

                    {/* Subtitle */}
                    <p style={{ 
                        fontSize: '14px', 
                        color: '#666666', 
                        marginBottom: '30px',
                        lineHeight: '1.5'
                    }}>
                        {chargeFailed 
                            ? <>Select an option to proceed</>
                            : <>Once these changes are confirmed, they<br />cannot be reversed.</>
                        }
                    </p>

                    {/* Charge Failed Options */}
                    {chargeFailed && differenceType === 'collect' && (
                        <div style={{ textAlign: 'left', marginBottom: '20px' }}>
                            <h4 style={{ 
                                fontWeight: '400', 
                                fontSize: '14px', 
                                marginBottom: '15px', 
                                color: '#424242' 
                            }}>
                                Options
                            </h4>

                            {/* Option 1: Retry with current card */}
                            <Box
                                onClick={() => {
                                    setSelectedFailedOption('retry');
                                    setShowCardForm(false);
                                }}
                                sx={{
                                    border: selectedFailedOption === 'retry' ? '1px solid #ECEBFF' : '1px solid #E0E0E0',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: selectedFailedOption === 'retry' ? '#F8F7FF' : '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginBottom: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {selectedFailedOption === 'retry' ? (
                                    <PiCheckCircleFill size={24} color="#7963FF" />
                                ) : (
                                    <Box sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        border: '2px solid #E0E0E0',
                                    }} />
                                )}
                                <div style={{ flex: 1 }}>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        fontWeight: '500', 
                                        color: '#424242', 
                                    }}>
                                        Retry Charge With Current Card
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#B9B9BA', lineHeight: '100%' }}>
                                        Amount To Charge: {formatPrice(differenceAmount)}
                                    </div>
                                </div>
                                <div className="card_logo_container">
                                    {cardLogoUrl && (
                                        <img 
                                            src={cardLogoUrl} 
                                            alt="Card" 
                                            style={{ 
                                                width: '40px', 
                                                height: '25px', 
                                                objectFit: 'contain' 
                                            }} 
                                        />
                                    )}
                                </div>
                            </Box>

                            {/* Option 2: Edit Payment Information - Show Form or Option */}
                            {showCardForm ? (
                                /* Card Form */
                                <Box
                                    sx={{
                                        border: '1px solid #ECEBFF',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        backgroundColor: '#F8F7FF',
                                        marginBottom: '12px',
                                    }}
                                >
                                    {/* Form Header */}
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        marginBottom: '16px'
                                    }}>
                                        <span style={{ 
                                            fontSize: '14px', 
                                            fontWeight: '500', 
                                            color: '#7963FF' 
                                        }}>
                                            Edit Card Information
                                        </span>
                                        <span style={{ 
                                            fontSize: '14px', 
                                            color: '#7963FF' 
                                        }}>
                                            Amount To Charge: {formatPrice(differenceAmount)}
                                        </span>
                                    </div>

                                    {/* Name On Card */}
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '12px',
                                        marginBottom: '12px'
                                    }}>
                                        <label style={{ 
                                            fontSize: '14px', 
                                            color: '#424242',
                                            minWidth: '100px'
                                        }}>
                                            Name On Card
                                        </label>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={cardFormData.nameOnCard}
                                            onChange={(e) => setCardFormData(prev => ({ ...prev, nameOnCard: e.target.value }))}
                                            sx={{
                                                backgroundColor: '#FFFFFF',
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    '& fieldset': {
                                                        borderColor: '#E0E0E0',
                                                    },
                                                },
                                            }}
                                        />
                                    </div>

                                    {/* Card Number */}
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '12px',
                                        marginBottom: '12px'
                                    }}>
                                        <label style={{ 
                                            fontSize: '14px', 
                                            color: '#424242',
                                            minWidth: '100px'
                                        }}>
                                            Card Number
                                        </label>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={cardFormData.cardNumber}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '').slice(0, 16);
                                                setCardFormData(prev => ({ ...prev, cardNumber: value }));
                                            }}
                                            placeholder=""
                                            sx={{
                                                backgroundColor: '#FFFFFF',
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    '& fieldset': {
                                                        borderColor: '#E0E0E0',
                                                    },
                                                },
                                            }}
                                        />
                                    </div>

                                    {/* Expiry and CVV */}
                                    <div style={{ 
                                        display: 'flex', 
                                        gap: '12px',
                                        marginLeft: '112px'
                                    }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ 
                                                fontSize: '14px', 
                                                color: '#424242',
                                                display: 'block',
                                                marginBottom: '8px'
                                            }}>
                                                MM/YY
                                            </label>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={cardFormData.expiryDate}
                                                onChange={(e) => {
                                                    let value = e.target.value.replace(/\D/g, '');
                                                    if (value.length > 2) {
                                                        value = value.slice(0, 2) + '/' + value.slice(2, 4);
                                                    }
                                                    setCardFormData(prev => ({ ...prev, expiryDate: value }));
                                                }}
                                                placeholder=""
                                                inputProps={{ maxLength: 5 }}
                                                sx={{
                                                    backgroundColor: '#FFFFFF',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        '& fieldset': {
                                                            borderColor: '#E0E0E0',
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ 
                                                fontSize: '14px', 
                                                color: '#424242',
                                                display: 'block',
                                                marginBottom: '8px'
                                            }}>
                                                CVV
                                            </label>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={cardFormData.cvv}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                                                    setCardFormData(prev => ({ ...prev, cvv: value }));
                                                }}
                                                placeholder=""
                                                type="password"
                                                sx={{
                                                    backgroundColor: '#FFFFFF',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        '& fieldset': {
                                                            borderColor: '#E0E0E0',
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                    </div>
                                </Box>
                            ) : (
                                /* Option 2 Box */
                                <Box
                                    onClick={() => {
                                        setSelectedFailedOption('edit');
                                    }}
                                    sx={{
                                        border: selectedFailedOption === 'edit' ? '1px solid #ECEBFF' : '1px solid #E0E0E0',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        backgroundColor: selectedFailedOption === 'edit' ? '#F8F7FF' : '#FFFFFF',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        marginBottom: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    {selectedFailedOption === 'edit' ? (
                                        <PiCheckCircleFill size={24} color="#7963FF" />
                                    ) : (
                                        <Box sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: '50%',
                                            border: '2px solid #E0E0E0',
                                        }} />
                                    )}
                                    <div style={{ flex: 1 }}>
                                        <div style={{ 
                                            fontSize: '14px', 
                                            fontWeight: '500', 
                                            color: '#424242', 
                                        }}>
                                            Edit Payment Information
                                        </div>
                                        <div style={{ fontSize: '14px', color: '#B9B9BA', lineHeight: '100%' }}>
                                            Amount To Charge: {formatPrice(differenceAmount)}
                                        </div>
                                    </div>
                                </Box>
                            )}

                            {/* Option 3: Send Payment Failed Notification */}
                            <Box
                                onClick={() => {
                                    setSelectedFailedOption('notify');
                                    setShowCardForm(false);
                                }}
                                sx={{
                                    border: selectedFailedOption === 'notify' ? '1px solid #ECEBFF' : '1px solid #E0E0E0',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: selectedFailedOption === 'notify' ? '#F8F7FF' : '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {selectedFailedOption === 'notify' ? (
                                    <PiCheckCircleFill size={24} color="#7963FF" />
                                ) : (
                                    <Box sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        border: '2px solid #E0E0E0',
                                    }} />
                                )}
                                <div style={{ flex: 1 }}>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        fontWeight: '500', 
                                        color: '#424242', 
                                    }}>
                                        Send Payment Failed Notification To Customer
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#B9B9BA', lineHeight: '100%' }}>
                                        Amount To Charge: {formatPrice(differenceAmount)}
                                    </div>
                                </div>
                            </Box>

                            {/* Error Message */}
                            <Box
                                sx={{
                                    backgroundColor: '#F8E3E3',
                                    borderRadius: '8px',
                                    padding: '10px',
                                    textAlign: 'center',
                                    maxWidth: '80%',
                                    margin: '30px auto',
                                }}
                            >
                                <p style={{ 
                                    fontSize: '11px', 
                                    color: '#D52626', 
                                    margin: 0,
                                    lineHeight: '1.5'
                                }}>
                                    {blocked 
                                        ? `Your 3rd attempt to charge this account has failed. Please try again in 24 hours.`
                                        : 'We could not process your charge, please update customer payment information or try again at a later date'
                                    }
                                </p>
                            </Box>
                        </div>
                    )}

                    {/* Normal Options Section (when charge hasn't failed) */}
                    {!chargeFailed && differenceType !== 'none' && (
                        <div style={{ textAlign: 'left', marginBottom: '30px' }}>
                            <h4 style={{ 
                                fontWeight: '400', 
                                fontSize: '14px', 
                                marginBottom: '15px', 
                                color: '#424242' 
                            }}>
                                Options
                            </h4>

                            <Box
                                sx={{
                                    border: '1px solid #ECEBFF',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: '#F8F7FF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                }}
                            >
                                <PiCheckCircleFill size={20} color="#7963FF" />
                                <div style={{ flex: 1 }}>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        fontWeight: '500', 
                                        color: '#424242', 
                                    }}>
                                        {optionText.title}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#B9B9BA', lineHeight: '100%' }}>
                                        {optionText.subtitle}
                                    </div>
                                </div>

                                <div className="card_logo_container">
                                    {cardLogoUrl && (
                                        <img 
                                            src={cardLogoUrl} 
                                            alt="Card" 
                                            style={{ 
                                                width: '40px', 
                                                height: '25px', 
                                                objectFit: 'contain' 
                                            }} 
                                        />
                                    )}
                                </div>
                            </Box>
                        </div>
                    )}

                    {/* Actions */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: '12px',
                        marginTop: '20px'
                    }}>
                        <Button
                            variant="text"
                            color="primary"
                            onClick={() => {
                                if (showCardForm) {
                                    setShowCardForm(false);
                                } else {
                                    setOpen(false);
                                }
                            }}
                            disabled={loading}
                            sx={{
                                borderRadius: '8px !important',
                                color: '#424242',
                                padding: '10px 24px !important',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            {showCardForm ? 'Back' : 'Cancel'}
                        </Button>
                        {chargeFailed ? (
                            showCardForm ? (
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        if (onEditPaymentInfo) {
                                            onEditPaymentInfo(cardFormData);
                                        }
                                    }}
                                    loading={loading}
                                    disabled={blocked || !cardFormData.nameOnCard || !cardFormData.cardNumber || !cardFormData.expiryDate || !cardFormData.cvv}
                                    sx={{
                                        borderRadius: '8px !important',
                                        padding: '10px 24px !important',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    Charge Card
                                </LoadingButton>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        if (selectedFailedOption === 'retry') {
                                            onConfirm();
                                        } else if (selectedFailedOption === 'edit') {
                                            setShowCardForm(true);
                                        } else if (selectedFailedOption === 'notify' && onSendFailedNotification) {
                                            onSendFailedNotification();
                                        }
                                    }}
                                    disabled={!selectedFailedOption || (blocked && selectedFailedOption !== 'notify')}
                                    sx={{
                                        borderRadius: '8px !important',
                                        padding: '10px 24px !important',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    Continue
                                </Button>
                            )
                        ) : (
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                onClick={onConfirm}
                                loading={loading}
                                sx={{
                                    borderRadius: '8px !important',
                                    padding: '10px 24px !important',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Confirm
                            </LoadingButton>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
